import React, {useState, useEffect, useRef} from 'react';
import {
  ScrollView,
  View,
  Text,
  TextInput,
  Pressable,
  Modal,
} from 'react-native';
import {usePowerSyncData} from '../../Providers/PowerSyncDataProvider';
import {useIsFocused} from '@react-navigation/native';
import Icon from '../Shared/Icon';
import {Template} from '../../Models/PowerSyncModels/models';
import {TemplateCategoryTypes} from '../../Constants/TemplateCategoryTypes';
import {MongoIdSqlId} from '../../Types/DtoTypes';
import {CATEGORY} from '../../Constants/AppConstants';
import {CommonStyleSheet} from '../../Styles/Shared/CommonStyles';
import Colors from '../../Styles/Shared/Colors';
import {ControlsStyleSheet} from '../../Styles/Shared/Controls';
import DynamicDatePicker from '../DynamicControls/DynamicDatePicker';
import DynamicAutocomplete from '../DynamicControls/DynamicAutocomplete';
import {ListDataTypes} from '../../Constants/ListDataTypes';
import {FilterTypes} from '../../Constants/General';
import DynamicSelect from '../DynamicControls/DynamicSelect';
import {Icons} from '../../Constants/Icons';
import {SortConfig, SortKey} from '../Views/Reports';
import GeneralHelperService from '../../Services/GeneralHelperService';
import { IGeneralHelperService } from '../../Services/Interfaces/IGeneralHelperService';

export declare type Sorter = {label: string; type: string};
export declare type Filter = {label: string; type: string};
export declare type FilterStatuses =
  | 'in-progress'
  | 'completed'
  | 'overdue'
  | 'dismissed';
export declare type ReportType = {
  id: string;
  name?: string;
  icon: string;
  isQuickAdd?: boolean;
};
export declare type FilterValue = {
  startDate?: string;
  endDate?: string;
  statuses?: FilterStatuses[];
  types?: string[];
  person?: string;
  firstAidAttendant?: string;
  project?: string;
  equipmentType?: string;
  equipment?: string;
  pendingInvestigation?: boolean;
  investigationAttached?: boolean;
};

type SearchBoxProps = {
  onChangeSearch?: (newSearch: string) => void;
  onChangeFilter?: (filterValue: FilterValue) => void;
  onFocus?: (position: number) => void;
  disabled?: boolean;
  showFilters?: boolean;
  showSorters?: boolean;
  filters?: Filter[];
  filterValue?: FilterValue;
  category?: string;
  sortConfig?: SortConfig;
  setSortConfig?: React.Dispatch<React.SetStateAction<SortConfig>>;
};

const SearchBox = (props: SearchBoxProps): React.ReactElement => {
  const [search, setSearch] = useState('');
  const [position, setPosition] = useState(0);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [showSorterModal, setShowSorterModal] = useState(false);
  const sorterButtonRef = useRef<React.ElementRef<typeof Pressable>>(null);
  const [sorterPosition, setSorterPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statuses, setStatuses] = useState<FilterStatuses[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [person, setPerson] = useState('');
  const [firstAidAttendant, setFirstAidAttendant] = useState('');
  const [firstAidAttendantVisible, setFirstAidAttendantVisible] =
    useState(false);
  const [project, setProject] = useState('');
  const [equipmentType, setEquipmentType] = useState('');
  const [equipment, setEquipment] = useState('');
  const [pendingInvestigation, setPendingInvestigation] = useState(false);
  const [investigationAttached, setInvestigationAttached] = useState(false);

  const [types, setTypes] = useState<ReportType[]>([]);

  const [filtersKey, setFiltersKey] = useState(0);

  const {templates, templateCategories, powersyncReady} = usePowerSyncData();
  const isFocused = useIsFocused();
  const helperService: IGeneralHelperService = new GeneralHelperService();
  
  useEffect(() => {
    const getReportTypes = async () => {
      let newTypes: ReportType[] = [];

      let reportTypeFilter = props.filters?.find(
        f => f.type === FilterTypes.REPORT_TYPE,
      );
      if (reportTypeFilter) {
        let filteredTemplates: Template[] = [];
        let quickaddTemplates: string[] = [];
        if (props.category == CATEGORY.REPORTS) {
          let quickadd = templates
            .filter(
              t =>
                t.templateCategory.templateCategoryName ===
                TemplateCategoryTypes.QUICKADD,
            )
            .sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0));
          quickaddTemplates = quickadd.map(q => q._id);

          let fullReports = templates
            .filter(
              t =>
                t.templateCategory.templateCategoryName ===
                TemplateCategoryTypes.REPORTS,
            )
            .sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0));

          filteredTemplates = quickadd.concat(fullReports);
        } else if (props.category == CATEGORY.PEOPLE) {
          let cat = templateCategories.find(c=>c.name === TemplateCategoryTypes.PEOPLE);
          filteredTemplates = templates
            .filter(
              t =>
                cat && cat.listTemplateIds && cat.listTemplateIds!.includes(t._id)
            )
            .sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0));
        } else if (props.category == CATEGORY.EQUIPMENT) {
          let cat = templateCategories.find(c=>c.name === TemplateCategoryTypes.EQUIPMENT);
          filteredTemplates = templates
            .filter(
              t =>
                cat && cat.listTemplateIds && cat.listTemplateIds!.includes(t._id)
            )
            .sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0));
        } else if (props.category == CATEGORY.OPERATIONS) {
          let cat = templateCategories.find(c=>c.name === TemplateCategoryTypes.OPERATIONS);
          filteredTemplates = templates
            .filter(
              t =>
                cat && cat.listTemplateIds && cat.listTemplateIds!.includes(t._id)
            )
            .sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0));
        }

        newTypes = filteredTemplates.map(t => ({
          id: t._id,
          name: t.name,
          icon: t.iconSvg
            ? helperService.getTemplateIcon(t.iconSvg, Colors.darkestGreen, 'secondaryColor')
            : '',
          isQuickAdd: quickaddTemplates.includes(t._id),
        }));
      }

      setTypes(newTypes);
    };

    getReportTypes();
  }, [powersyncReady]);

  useEffect(() => {
    if (props.filterValue) {
      if (props.filterValue.startDate)
        setStartDate(props.filterValue.startDate);

      if (props.filterValue.endDate) setEndDate(props.filterValue.endDate);

      if (props.filterValue.statuses) setStatuses(props.filterValue.statuses);

      if (props.filterValue.types) setSelectedTypes(props.filterValue.types);

      if (props.filterValue.person) setPerson(props.filterValue.person);

      if (props.filterValue.firstAidAttendant)
        setFirstAidAttendant(props.filterValue.firstAidAttendant);

      if (props.filterValue.project) setProject(props.filterValue.project);

      if (props.filterValue.equipmentType)
        setEquipmentType(props.filterValue.equipmentType);

      if (props.filterValue.equipment)
        setEquipment(props.filterValue.equipment);

      if (props.filterValue.pendingInvestigation)
        setPendingInvestigation(props.filterValue.pendingInvestigation);

      if (props.filterValue.pendingInvestigation)
        setPendingInvestigation(props.filterValue.pendingInvestigation);

      if (props.filterValue.investigationAttached)
        setInvestigationAttached(props.filterValue.investigationAttached);
    }
  }, [props.filterValue]);

  useEffect(() => {
    if (selectedTypes) {
      let filteredTemplates = templates.filter(t =>
        selectedTypes!.includes(t._id),
      );

      let injurySelected = filteredTemplates.find(
        x => x.name == 'Full - Injury',
      );

      if (injurySelected) setFirstAidAttendantVisible(true);
      else {
        setFirstAidAttendant('');
        setFirstAidAttendantVisible(false);
      }
    }
  }, [selectedTypes]);

  useEffect(() => {
    if (!isFocused) {
      clearFilters();
    }
  }, [isFocused]);

  function change(newSearch: string): void {
    setSearch(newSearch);
    if (props.onChangeSearch) props.onChangeSearch(newSearch);
  }

  function focus(): void {
    if (props.onFocus) props.onFocus(position);
  }

  function clear(): void {
    change('');
  }

  function showFilters(): void {
    setShowFiltersModal(true);
  }

  function showSorters(): void {
    if (!sorterButtonRef.current) {
      return;
    }

    sorterButtonRef.current.measure(
      (
        fx: number,
        fy: number,
        width: number,
        height: number,
        px: number,
        py: number,
      ) => {
        setSorterPosition({top: py + height, left: px, width: width});
        setShowSorterModal(true);
      },
    );
  }

  function clearFilters(): void {
    setStartDate('');
    setEndDate('');
    setStatuses([]);
    setSelectedTypes([]);
    setPerson('');
    setFirstAidAttendant('');
    setProject('');
    setEquipmentType('');
    setEquipment('');
    setPendingInvestigation(false);
    setInvestigationAttached(false);

    if (props.onChangeFilter)
      props.onChangeFilter({
        startDate: '',
        endDate: '',
        statuses: [],
        types: [],
        person: '',
        firstAidAttendant: '',
        project: '',
        equipmentType: '',
        equipment: '',
        pendingInvestigation: false,
        investigationAttached: false,
      });

    // Due to performance issues components are not listening for changes in values
    // So we need to force a re-render after cleaning filters
    setFiltersKey(filtersKey + 1);
  }

  function applyFilters(): void {
    if (props.onChangeFilter)
      props.onChangeFilter({
        startDate: startDate,
        endDate: endDate,
        statuses: statuses,
        types: selectedTypes,
        person: person,
        firstAidAttendant: firstAidAttendant,
        project: project,
        equipmentType: equipmentType,
        equipment: equipment,
        pendingInvestigation: pendingInvestigation,
        investigationAttached: investigationAttached,
      });

    setShowFiltersModal(false);
  }

  function toggleStatus(status: FilterStatuses): void {
    let selectedStatuses = [...statuses];
    let index = selectedStatuses.indexOf(status);

    if (index !== -1) selectedStatuses.splice(index, 1);
    else selectedStatuses.push(status);

    setStatuses(selectedStatuses);
  }

  function toggleSelectedTypes(typeId: string): void {
    let newSelectedTypes = [...selectedTypes];

    let index = newSelectedTypes.indexOf(typeId);
    if (index !== -1) newSelectedTypes.splice(index, 1);
    else newSelectedTypes.push(typeId);

    setSelectedTypes(newSelectedTypes);
  }

  const renderFiltersModal = (): React.ReactElement => {
    return (
      <Modal
        visible={showFiltersModal}
        transparent={true}
        statusBarTranslucent={true}
        animationType='fade'>
        <View style={ControlsStyleSheet.modalBackground}>
          <View key={filtersKey} style={ControlsStyleSheet.groupSelectorModal}>
            <View style={ControlsStyleSheet.groupSelectorModalBar}>
              <Text style={ControlsStyleSheet.groupSelectorModalTitle}>
                Filter
              </Text>
              <Pressable
                style={({pressed}) => [
                  ControlsStyleSheet.groupSelectorModalClose,
                  pressed && {
                    backgroundColor: Colors.darkGreenTransparent,
                    borderRadius: 24,
                  },
                ]}
                onPress={() => setShowFiltersModal(false)}>
                <Icon icon={'close'} color={Colors.darkestGreen} size={24} />
              </Pressable>
            </View>
            <ScrollView style={{marginVertical: 20}}>
              {renderDateRangeFilter()}
              {renderTaskStatusFilter()}
              {renderPersonFilter()}
              {renderFirstAidAttendantFilter()}
              {renderProjectFilter()}
              {renderEquipmentFilters()}
              {renderReportTypeFilter()}
            </ScrollView>
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                paddingVertical: 20,
              }}>
              <Pressable
                style={({pressed}) => [
                  CommonStyleSheet.greenButton,
                  pressed && {
                    opacity: 0.8,
                  },
                ]}
                onPress={clearFilters}>
                <Text style={CommonStyleSheet.greenButtonText}>Clear</Text>
              </Pressable>
              <Pressable
                style={({pressed}) => [
                  CommonStyleSheet.greenButton,
                  pressed && {
                    opacity: 0.8,
                  },
                ]}
                onPress={applyFilters}>
                <Text style={CommonStyleSheet.greenButtonText}>Apply</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  const renderSorterModal = (): React.ReactElement => {
    if (!props.sortConfig || !props.setSortConfig) {
      return <></>;
    }
    const {sortConfig, setSortConfig} = props;
    const sortOptions: {key: SortKey; label: string}[] = [
      {key: 'date', label: 'Date'},
      {key: 'incidentId', label: 'Incident ID'},
      {key: 'az', label: 'A-Z'},
    ];

    const handleSort = (newKey: SortKey) => {
      if (newKey === sortConfig.key) {
        const dir =
          sortConfig.direction === 'ascending' ? 'descending' : 'ascending';
        setSortConfig({key: newKey, direction: dir});
      } else {
        const descKeys: SortKey[] = ['date', 'incidentId', 'az'];
        setSortConfig({
          key: newKey,
          direction: descKeys.includes(newKey) ? 'descending' : 'ascending',
        });
      }
      setShowSorterModal(false);
    };

    return (
      <Modal
        transparent={true}
        visible={showSorterModal}
        onRequestClose={() => setShowSorterModal(false)}>
        <Pressable
          style={CommonStyleSheet.modalOverlay}
          onPress={() => setShowSorterModal(false)}>
          <View
            style={[
              CommonStyleSheet.menu,
              {
                top: sorterPosition.top,
                left: sorterPosition.left,
              },
            ]}>
            {sortOptions.map(option => (
              <Pressable
                key={option.key}
                style={[
                  CommonStyleSheet.button,
                  option.key === sortConfig.key &&
                    CommonStyleSheet.buttonActive,
                ]}
                onPress={() => handleSort(option.key)}>
                <Text
                  style={[
                    CommonStyleSheet.text,
                    option.key === sortConfig.key &&
                      CommonStyleSheet.textActive,
                  ]}>
                  {option.label}
                  {option.key === sortConfig.key
                    ? sortConfig.direction === 'ascending'
                      ? ' ↑'
                      : ' ↓'
                    : ''}
                </Text>
              </Pressable>
            ))}
          </View>
        </Pressable>
      </Modal>
    );
  };

  const renderDateRangeFilter = (): React.ReactElement => {
    let dateRangeFilter = props.filters?.find(
      f => f.type === FilterTypes.DATE_RANGE,
    );

    if (!dateRangeFilter) return <></>;

    return (
      <View>
        <Text style={ControlsStyleSheet.label}>{dateRangeFilter.label}</Text>
        <View style={{flexDirection: 'row', gap: 12}}>
          <View style={{flex: 1}}>
            <Text style={[ControlsStyleSheet.label, {marginBottom: -16}]}>
              From:
            </Text>
            <DynamicDatePicker
              value={startDate}
              config={{maxDate: endDate}}
              onChange={(controlId, controlTypeId, value) => {
                setStartDate(value);
              }}
            />
          </View>
          <View style={{flex: 1}}>
            <Text style={[ControlsStyleSheet.label, {marginBottom: -16}]}>
              To:
            </Text>
            <DynamicDatePicker
              value={endDate}
              config={{minDate: startDate}}
              onChange={(controlId, controlTypeId, value) => {
                setEndDate(value);
              }}
            />
          </View>
        </View>
      </View>
    );
  };

  const renderTaskStatusFilter = (): React.ReactElement => {
    let taskStatusFilter = props.filters?.find(
      f => f.type === FilterTypes.TASK_STATUS,
    );

    if (!taskStatusFilter) return <></>;

    return (
      <View style={CommonStyleSheet.statusFilterContainer}>
        <Pressable
          style={({pressed}) => [
            CommonStyleSheet.statusFilterPill,
            {
              borderColor: Colors.green,
              backgroundColor: Colors.white,
            },
            pressed && {opacity: 0.8},
          ]}
          onPress={() => toggleStatus('in-progress')}>
          <Text
            style={[
              CommonStyleSheet.statusFilterText,
              {
                color: Colors.green,
              },
            ]}>
            In Progress
          </Text>
          <View
            style={[
              CommonStyleSheet.statusFilterCheckBox,
              statuses.includes('in-progress') && {
                backgroundColor: Colors.darkestGreen,
              },
            ]}>
            <Icon icon={'checked'} color={Colors.white} size={16} />
          </View>
        </Pressable>
        <Pressable
          style={({pressed}) => [
            CommonStyleSheet.statusFilterPill,
            {
              borderColor: Colors.green,
              backgroundColor: Colors.green,
            },
            pressed && {opacity: 0.8},
          ]}
          onPress={() => toggleStatus('completed')}>
          <Text
            style={[
              CommonStyleSheet.statusFilterText,
              {
                color: Colors.white,
              },
            ]}>
            Completed
          </Text>
          <View
            style={[
              CommonStyleSheet.statusFilterCheckBox,
              statuses.includes('completed') && {
                backgroundColor: Colors.darkestGreen,
              },
            ]}>
            <Icon icon={'checked'} color={Colors.white} size={16} />
          </View>
        </Pressable>
        <View style={{flexBasis: '100%'}}></View>
        <Pressable
          style={({pressed}) => [
            CommonStyleSheet.statusFilterPill,
            {
              borderColor: Colors.red,
              backgroundColor: Colors.red,
            },
            pressed && {opacity: 0.8},
          ]}
          onPress={() => toggleStatus('overdue')}>
          <Text
            style={[
              CommonStyleSheet.statusFilterText,
              {
                color: Colors.white,
              },
            ]}>
            Overdue
          </Text>
          <View
            style={[
              CommonStyleSheet.statusFilterCheckBox,
              statuses.includes('overdue') && {
                backgroundColor: Colors.darkestGreen,
              },
            ]}>
            <Icon icon={'checked'} color={Colors.white} size={16} />
          </View>
        </Pressable>
        <Pressable
          style={({pressed}) => [
            CommonStyleSheet.statusFilterPill,
            {
              borderColor: Colors.gray,
              backgroundColor: Colors.gray,
            },
            pressed && {opacity: 0.8},
          ]}
          onPress={() => toggleStatus('dismissed')}>
          <Text
            style={[
              CommonStyleSheet.statusFilterText,
              {
                color: Colors.white,
              },
            ]}>
            Dismissed
          </Text>
          <View
            style={[
              CommonStyleSheet.statusFilterCheckBox,
              statuses.includes('dismissed') && {
                backgroundColor: Colors.darkestGreen,
              },
            ]}>
            <Icon icon={'checked'} color={Colors.white} size={16} />
          </View>
        </Pressable>
      </View>
    );
  };

  const renderReportTypeFilter = (): React.ReactElement => {
    let reportTypeFilter = props.filters?.find(
      f => f.type === FilterTypes.REPORT_TYPE,
    );

    let reportInvFilter = props.filters?.find(
      f => f.type === FilterTypes.INVESTIGATION,
    );

    if (!reportTypeFilter || types.length === 0) return <></>;

    return (
      <View>
        <Text style={ControlsStyleSheet.label}>{reportTypeFilter!.label}</Text>
        <View
          style={{
            flexDirection: 'row',
            flexWrap: 'wrap',
            rowGap: 6,
            justifyContent: 'space-between',
          }}>
          {types.map((t, index) => (
            <View
              key={index}
              style={{
                flexBasis: '46%',
                flexDirection: 'row',
                flexWrap: 'nowrap',
                alignItems: 'center',
                gap: 6,
              }}>
              <Pressable
                id={t.name}
                style={({pressed}) => [
                  CommonStyleSheet.filterButton,
                  {
                    backgroundColor: Colors.lightgray,
                    width: 64,
                    height: 64,
                    borderRadius: 64,
                  },
                  selectedTypes.includes(t.id) && {
                    backgroundColor: Colors.teal,
                  },
                  pressed && {
                    opacity: 0.8,
                  },
                ]}
                onPress={() => toggleSelectedTypes(t.id)}>
                {t.icon && (
                  <img
                    src={`data:image/svg+xml;utf8,${encodeURIComponent(
                      t.icon.replaceAll(
                        'secondaryColor',
                        selectedTypes.includes(t.id)
                          ? Colors.teal
                          : Colors.lightgray,
                      ),
                    )}`}
                    style={{width: 28}}
                  />
                )}
              </Pressable>
              <Text style={[ControlsStyleSheet.label, {marginTop: 0}]}>
                {t.name
                  ?.replace('Full - ', '')
                  .replace('Add ', '')
                  .replace('Create a ', '')}
                {t.isQuickAdd && ' (Quick Report)'}
              </Text>
            </View>
          ))}
          {reportInvFilter && (
            <>
              <View
                style={{
                  flexBasis: '46%',
                  flexDirection: 'row',
                  flexWrap: 'nowrap',
                  alignItems: 'center',
                  gap: 6,
                }}>
                <Pressable
                  style={({pressed}) => [
                    CommonStyleSheet.filterButton,
                    {
                      backgroundColor: Colors.lightgray,
                      width: 64,
                      height: 64,
                      borderRadius: 64,
                    },
                    pendingInvestigation && {
                      backgroundColor: Colors.teal,
                    },
                    pressed && {
                      opacity: 0.8,
                    },
                  ]}
                  onPress={() =>
                    setPendingInvestigation(!pendingInvestigation)
                  }>
                  <img
                    src={`data:image/svg+xml;utf8,${encodeURIComponent(
                      Icons.PENDING_INVESTIGATION.replaceAll(
                        Colors.red,
                        Colors.darkestGreen,
                      ),
                    )}`}
                    style={{width: 16}}
                  />
                </Pressable>
                <Text style={[ControlsStyleSheet.label, {marginTop: 0}]}>
                  Pending Investigation
                </Text>
              </View>
              <View
                style={{
                  flexBasis: '46%',
                  flexDirection: 'row',
                  flexWrap: 'nowrap',
                  alignItems: 'center',
                  gap: 6,
                }}>
                <Pressable
                  style={({pressed}) => [
                    CommonStyleSheet.filterButton,
                    {
                      backgroundColor: Colors.lightgray,
                      width: 64,
                      height: 64,
                      borderRadius: 64,
                    },
                    investigationAttached && {
                      backgroundColor: Colors.teal,
                    },
                    pressed && {
                      opacity: 0.8,
                    },
                  ]}
                  onPress={() =>
                    setInvestigationAttached(!investigationAttached)
                  }>
                  <img
                    src={`data:image/svg+xml;utf8,${encodeURIComponent(
                      Icons.INVESTIGATION.replaceAll(
                        Colors.red,
                        Colors.darkestGreen,
                      ),
                    )}`}
                    style={{width: 34}}
                  />
                </Pressable>
                <Text style={[ControlsStyleSheet.label, {marginTop: 0}]}>
                  Investigation Attached
                </Text>
              </View>
            </>
          )}
        </View>
      </View>
    );
  };

  const renderPersonFilter = (): React.ReactElement => {
    let personFilter = props.filters?.find(f => f.type === FilterTypes.PERSON);

    if (!personFilter) return <></>;

    return (
      <DynamicAutocomplete
        label={personFilter.label}
        config={{optionSource: ListDataTypes.PEOPLE}}
        value={person ? person : ''}
        onChange={(controlId, controlTypeId, value) => setPerson(value)}
      />
    );
  };

  const renderFirstAidAttendantFilter = (): React.ReactElement => {
    let firstAidAttendantFilter = props.filters?.find(
      f => f.type === FilterTypes.FIRST_AID_ATTENDANT,
    );

    if (!firstAidAttendantFilter || !firstAidAttendantVisible) return <></>;

    return (
      <DynamicAutocomplete
        label={firstAidAttendantFilter.label}
        config={{optionSource: ListDataTypes.PEOPLE}}
        value={firstAidAttendant ? firstAidAttendant : ''}
        onChange={(controlId, controlTypeId, value) =>
          setFirstAidAttendant(value)
        }
      />
    );
  };

  const renderProjectFilter = (): React.ReactElement => {
    let projectFilter = props.filters?.find(
      f => f.type === FilterTypes.PROJECT,
    );

    if (!projectFilter) return <></>;

    return (
      <DynamicAutocomplete
        label={projectFilter.label}
        config={{optionSource: ListDataTypes.PROJECTS}}
        value={project ? project : ''}
        onChange={(controlId, controlTypeId, value) => setProject(value)}
      />
    );
  };

  const renderEquipmentFilters = (): React.ReactElement => {
    let equipmentFilter = props.filters?.find(
      f => f.type === FilterTypes.EQUIPMENT,
    );

    if (!equipmentFilter) return <></>;

    return (
      <>
        <DynamicSelect
          label='Equipment Type'
          config={{
            options: [
              {
                label: 'Vehicles',
                value: ListDataTypes.EQUIPMENTVEHICLEPROFILES,
              },
              {
                label: 'Heavy Equipment',
                value: ListDataTypes.EQUIPMENTHEAVYPROFILES,
              },
              {label: 'Other', value: ListDataTypes.EQUIPMENTOTHERPROFILES},
            ],
          }}
          value={equipmentType}
          onChange={(controlId, controlTypeId, value) => {
            setEquipmentType(value);
          }}
        />
        {!equipmentType && (
          //This control is here to let the users know that if they select an equipment type
          //then they can filter by a specific equipment as well
          <DynamicAutocomplete
            label='Equipment'
            value={
              '{"label": "Select an equipment type to see options", "mongoId":""}'
            }
            disabled={true}
          />
        )}
        {equipmentType === ListDataTypes.EQUIPMENTVEHICLEPROFILES && (
          <DynamicAutocomplete
            label={'Vehicle'}
            config={{optionSource: ListDataTypes.EQUIPMENTVEHICLEPROFILES}}
            value={equipment ? equipment : ''}
            onChange={(controlId, controlTypeId, value) => setEquipment(value)}
          />
        )}
        {equipmentType === ListDataTypes.EQUIPMENTHEAVYPROFILES && (
          <DynamicAutocomplete
            label={'Heavy Equipment'}
            config={{optionSource: ListDataTypes.EQUIPMENTHEAVYPROFILES}}
            value={equipment ? equipment : ''}
            onChange={(controlId, controlTypeId, value) => setEquipment(value)}
          />
        )}
        {equipmentType === ListDataTypes.EQUIPMENTOTHERPROFILES && (
          <DynamicAutocomplete
            label={'Other Equipment'}
            config={{optionSource: ListDataTypes.EQUIPMENTOTHERPROFILES}}
            value={equipment ? equipment : ''}
            onChange={(controlId, controlTypeId, value) => setEquipment(value)}
          />
        )}
      </>
    );
  };

  return (
    <View
      onLayout={e => setPosition(e.nativeEvent.layout.y)}
      style={CommonStyleSheet.searchContainer}>
      <View style={CommonStyleSheet.searchBoxContainer}>
        <View style={CommonStyleSheet.searchBoxIcon}>
          <Icon icon={'input-search'} color={Colors.darkGreen} size={24} />
        </View>
        <TextInput
          returnKeyType='search'
          style={CommonStyleSheet.searchBoxInput}
          placeholder='Search'
          placeholderTextColor={Colors.gray}
          value={search}
          onChangeText={newText => change(newText)}
          onFocus={() => focus()}
          editable={props.disabled !== true}
        />
        <Pressable
          style={({pressed}) => [
            CommonStyleSheet.searchBoxClear,
            {display: search === '' ? 'none' : 'flex'},
            pressed && {
              backgroundColor: Colors.darkGreenTransparent,
              borderRadius: 14,
            },
          ]}
          onPress={clear}>
          <Icon icon={'input-clear'} color={Colors.darkGreen} size={18} />
        </Pressable>
      </View>

      <View style={{flexDirection: 'row', gap: 5}}>
        {props.showFilters && (
          <View style={[CommonStyleSheet.searchFilters]}>
            <Pressable
              style={({pressed}) => [
                CommonStyleSheet.searchFiltersContent,
                pressed && {
                  backgroundColor: Colors.darkGreenTransparent,
                  borderRadius: 40,
                },
                (startDate !== '' ||
                  endDate !== '' ||
                  statuses.length > 0 ||
                  selectedTypes.length > 0 ||
                  person !== '' ||
                  firstAidAttendant !== '' ||
                  project !== '' ||
                  equipmentType !== '' ||
                  equipment !== '' ||
                  pendingInvestigation ||
                  investigationAttached) && {
                  backgroundColor: Colors.yellow,
                  borderRadius: 40,
                },
              ]}
              onPress={showFilters}>
              <Text style={CommonStyleSheet.searchFiltersText}>Filter</Text>
              <Icon icon={'input-filters'} color={Colors.green} size={18} />
            </Pressable>
            {renderFiltersModal()}
          </View>
        )}
        {props.showSorters && (
          <View style={[CommonStyleSheet.searchFilters]}>
            <Pressable
              ref={sorterButtonRef}
              style={({pressed}) => [
                CommonStyleSheet.searchFiltersContent,
                pressed && {
                  backgroundColor: Colors.darkGreenTransparent,
                  borderRadius: 40,
                },
              ]}
              onPress={showSorters}>
              <Text style={CommonStyleSheet.searchFiltersText}>Sort</Text>
              <Icon icon={'sorter'} color={Colors.green} size={18} />
            </Pressable>
            {renderSorterModal()}
          </View>
        )}
      </View>
    </View>
  );
};

export default SearchBox;
